import "./instrument";
import { StrictMode } from "react";
import ReactDOM from "react-dom/client";
import { RouterProvider, createRouter } from "@tanstack/react-router";
import "./global.css";
import * as Sentry from "@sentry/react";

// Import the generated route tree
import { routeTree } from "./routeTree.gen";
import { installBuildVersionCheck } from "./lib/build-version";

// Auto-reload on stale chunk errors (e.g. after deployment with cached HTML)
function isChunkLoadError(error: unknown): boolean {
  if (!(error instanceof Error)) return false;
  return (
    error.message.includes("Failed to fetch dynamically imported module") ||
    error.message.includes("is not a valid JavaScript MIME type") ||
    error.message.includes("Importing a module script failed") ||
    error.name === "ChunkLoadError"
  );
}

// A chunk that is genuinely missing would otherwise reload forever, so allow
// at most one chunk-error reload per 10 seconds.
function reloadForChunkError(): void {
  const key = "chunk-reload-at";
  try {
    const last = Number(sessionStorage.getItem(key) ?? 0);
    if (Date.now() - last < 10_000) return;
    sessionStorage.setItem(key, String(Date.now()));
  } catch {
    // Storage unavailable; reload anyway.
  }
  window.location.reload();
}

window.addEventListener("unhandledrejection", (event) => {
  if (isChunkLoadError(event.reason)) reloadForChunkError();
});

window.addEventListener("error", (event) => {
  if (isChunkLoadError(event.error)) reloadForChunkError();
});

// Vite's own signal for a failed lazy-chunk preload. Never preventDefault():
// Vite then resolves the dynamic import to `undefined` instead of rejecting,
// and the router crashes reading the route's export off it.
window.addEventListener("vite:preloadError", () => {
  reloadForChunkError();
});

// Create a new router instance
const router = createRouter({
  routeTree,
  defaultOnCatch: (error) => {
    if (isChunkLoadError(error)) reloadForChunkError();
  },
});

installBuildVersionCheck(router);

// Register the router instance for type safety
declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}

// Render the app
const rootElement = document.getElementById("root")!;
if (!rootElement.innerHTML) {
  const root = ReactDOM.createRoot(rootElement, {
    // Callback called when an error is thrown and not caught by an ErrorBoundary.
    onUncaughtError: Sentry.reactErrorHandler((error, errorInfo) => {
      console.warn("Uncaught error", error, errorInfo.componentStack);
    }),
    // Callback called when React catches an error in an ErrorBoundary.
    onCaughtError: Sentry.reactErrorHandler(),
    // Callback called when React automatically recovers from errors.
    onRecoverableError: Sentry.reactErrorHandler(),
  });
  root.render(
    <StrictMode>
      <RouterProvider router={router} />
    </StrictMode>,
  );
}

// Register a no-op service worker to enable PWA install/fullscreen
if ("serviceWorker" in navigator) {
  window.addEventListener("load", () => {
    navigator.serviceWorker.register("/sw.js").catch(() => {
      // Ignore registration errors for now
    });
  });
}
